import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  Lock, 
  Eye, 
  Zap, 
  RefreshCw, 
  FileLock2, 
  Activity, 
  ChevronRight,
  ArrowRight
} from 'lucide-react';

export default function SecurityCompliance() {
  const policies = [
    {
      title: "Information Security",
      summary: "Our foundational commitment to protecting confidentiality, integrity, and availability through industry-standard frameworks like SOC 2 and PCI DSS.",
      path: "/security-policy",
      icon: ShieldCheck
    },
    {
      title: "Access Control",
      summary: "Strict adherence to the principle of least privilege, enforced via Multi-Factor Authentication (MFA) and rigorous identity management.",
      path: "/access-control-policy",
      icon: Lock
    },
    {
      title: "Vulnerability Management",
      summary: "Proactive identification and rapid remediation of system weaknesses before they can be exploited by emerging threats.",
      path: "/vulnerability-management-policy",
      icon: Zap
    },
    {
      title: "Logging & Monitoring",
      summary: "Continuous visibility into system activity to detect and neutralize suspicious behavior and unauthorized access in real-time.",
      path: "/logging-monitoring-policy",
      icon: Eye
    },
    {
      title: "Business Continuity",
      summary: "High-availability architecture designed for survival, ensuring rapid service restoration and total data integrity after disruptions.",
      path: "/business-continuity-policy",
      icon: RefreshCw
    },
    {
      title: "Data Protection",
      summary: "End-to-end encryption and responsible data handling practices to safeguard your most sensitive business and client assets.",
      path: "/data-protection-policy",
      icon: FileLock2
    },
    {
      title: "Incident Response",
      summary: "A battle-tested 6-stage lifecycle for rapid detection, containment, and mitigation of any security-related events.",
      path: "/incident-response-policy",
      icon: Activity
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-32 pb-24">
      <div className="container mx-auto px-4 md:px-8">
        <div className="max-w-6xl mx-auto space-y-16">
          {/* Hero Section */}
          <div className="space-y-6 text-center max-w-3xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]"
            >
              Enterprise Grade Trust
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-7xl font-display font-bold tracking-tight leading-tight"
            >
              Security & <span className="text-accent">Compliance</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-muted-foreground text-lg md:text-xl"
            >
              Agrandintl operates with a "Security-First" mindset. Our governance framework is built on industry best practices to ensure your data and operations are protected by world-class standards.
            </motion.p>
          </div>

          {/* Compliance Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {policies.map((policy, idx) => (
              <motion.div
                key={policy.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
                className="group"
              >
                <Link 
                  to={policy.path}
                  className="block h-full glass-card p-8 rounded-[2rem] border-white/5 bg-white/[0.01] hover:bg-white/[0.03] hover:border-accent/20 transition-all duration-500"
                >
                  <div className="space-y-6">
                    <div className="w-12 h-12 rounded-2xl bg-accent/10 flex items-center justify-center text-accent group-hover:scale-110 transition-transform duration-500">
                      <policy.icon className="w-6 h-6" />
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-xl font-display font-bold group-hover:text-accent transition-colors">
                        {policy.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {policy.summary}
                      </p>
                    </div>
                    <div className="pt-4 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-accent/60 group-hover:text-accent transition-colors">
                      Review Policy <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}

            {/* Privacy & Terms Callout */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="md:col-span-2 lg:col-span-1"
            >
              <div className="h-full bg-accent/5 p-8 rounded-[2rem] border border-accent/10 flex flex-col justify-between">
                <div className="space-y-4">
                  <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-accent">Legal Foundation</h3>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    Our commitment to your security is matched by our dedication to transparency and privacy.
                  </p>
                </div>
                <div className="mt-8 space-y-3">
                  <Link to="/privacy-policy" className="flex items-center justify-between p-4 rounded-xl bg-background/50 border border-white/5 hover:border-accent/30 transition-all group">
                    <span className="text-xs font-bold uppercase tracking-widest">Privacy Policy</span>
                    <ArrowRight className="w-4 h-4 text-accent transition-transform group-hover:translate-x-1" />
                  </Link>
                  <Link to="/terms-of-service" className="flex items-center justify-between p-4 rounded-xl bg-background/50 border border-white/5 hover:border-accent/30 transition-all group">
                    <span className="text-xs font-bold uppercase tracking-widest">Terms of Service</span>
                    <ArrowRight className="w-4 h-4 text-accent transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Trust Banner */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="p-12 rounded-[3rem] bg-gradient-to-br from-accent/10 via-transparent to-transparent border border-white/5 text-center space-y-6"
          >
            <div className="max-w-2xl mx-auto space-y-4">
              <h2 className="text-2xl md:text-3xl font-display font-bold">Have Specific Compliance Requirements?</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                We specialize in helping businesses navigate complex regulatory environments. Whether you need SOC 2 readiness, PCI DSS compliance, or customized security audits, our team is ready to assist.
              </p>
              <div className="pt-4">
                <Link to="/contact" className="btn-accent px-8 py-4 inline-flex">
                  Schedule a Consultation
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
